import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useStore } from '../../store';
import { Drone } from '../../types';
import DroneCard from './DroneCard';
import './DronePanel.css';

const GET_WL = gql`
  query GetWLPanel { whitelist { droneId } }
`;

type Tab = 'all' | 'position' | 'direction' | 'detection';
interface Props { onDroneSelect: (id: string) => void; }

export default function DronePanel({ onDroneSelect }: Props) {
  const [tab, setTab]     = useState<Tab>('all');
  const drones            = useStore(s => Array.from(s.drones.values()));
  const alarmedDrones     = useStore(s => s.alarmedDrones);
  const { data: wlData }  = useQuery(GET_WL, { pollInterval: 30000 });
  const wlIds             = new Set<string>((wlData?.whitelist || []).map((w: any) => w.droneId));
  const hasAlarm          = alarmedDrones.size > 0;
  const hasDefense        = [...alarmedDrones.values()].some(s => s === 'DEFENSE');

  const filtered = drones.filter((d: Drone) => {
    if (tab === 'position')  return d.lat && d.lng;
    if (tab === 'direction') return !d.lat && d.directionDeg;
    if (tab === 'detection') return d.sensorType === 'CAMERA';
    return true;
  });

  const zoneOrder = (id: string) => {
    const s = alarmedDrones.get(id);
    if (s === 'DEFENSE') return 0;
    if (s === 'WARNING') return 1;
    return 2;
  };
  const sorted = [...filtered].sort((a, b) => zoneOrder(a.droneId) - zoneOrder(b.droneId));

  const counts = {
    all:       drones.length,
    position:  drones.filter((d: Drone) => d.lat && d.lng).length,
    direction: drones.filter((d: Drone) => !d.lat && d.directionDeg).length,
    detection: drones.filter((d: Drone) => d.sensorType === 'CAMERA').length,
  };

  const ledClass = hasDefense ? 'led-defense' : hasAlarm ? 'led-warning' : drones.length > 0 ? 'led-active' : 'led-clear';

  return (
    <div className="drone-panel">
      <div className="panel-header">
        <span className="panel-title">Detected Drones</span>
        <span className={`panel-alert ${hasAlarm ? 'alarm-active' : ''}`}>
          <span className={`led-indicator ${ledClass}`} />
          {hasAlarm ? 'ALERT' : drones.length > 0 ? 'ACTIVE' : 'CLEAR'}
        </span>
      </div>
      <div className="panel-tabs">
        {(['all', 'position', 'direction', 'detection'] as Tab[]).map(t => (
          <button
            key={t}
            className={`panel-tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
            <span className="tab-count">{counts[t]}</span>
          </button>
        ))}
      </div>
      <div className="panel-list">
        {sorted.length === 0 ? (
          <div className="panel-empty">No drones detected</div>
        ) : (
          sorted.map((drone: Drone) => (
            <DroneCard
              key={drone.droneId}
              drone={drone}
              isWhitelisted={wlIds.has(drone.droneId)}
              onClick={() => onDroneSelect(drone.droneId)}
            />
          ))
        )}
      </div>
    </div>
  );
}
